import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'

import injectStyles from 'react-jss'
import { Grid } from '@material-ui/core'
import { Row, Col } from 'react-bootstrap'
import SubmitButton from 'Components/Form/SubmitButton'
import { Input } from 'Components/Form/Input'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import {
  AMBASSADOR_STATUS_KEY,
  AMBASSADOR_STATUSES,
  BLOCK_AMBASSADOR_REASON_OPTIONS,
  BLOCK_AMBASSADOR_REASON_OTHER,
} from 'Reducers/Ambassador/const'
import { styles } from './style'
import { showReduxModal } from '../../../../../Reducers/UI/actions'
import { useDispatch } from 'react-redux'
import GenericFlatButton from '../../../../../Components/GenericFlatButton'
import {
  STATUS_DOCUMENT_PENDING,
  STATUS_DOCUMENT_REJECTED,
} from '../../../../../shared/constants'
import { createUserProfileLog } from '../../Ambasador/gql'
import { authorization } from '../../../../../Module/Auth/Auth'
import { USER_PROFILE_LOGS_TYPE } from '../../../../../constants'

const AmbassadorStatusChange = ({
  ambassador,
  onChange,
  documents,
  onRefuse,
  disabled,
  classes,
}) => {
  const [blockReasonOption, setBlockReasonOption] = useState('')
  const [blockCustomText, setBlockCustomText] = useState('')
  const [showDisableForm, setShowDisableForm] = useState(false)
  const [forceUnbook, setForceUnbook] = useState(false)
  const [disablingSubmit, setDisablingSubmit] = useState(false)

  const { status, id } = ambassador
  const user = authorization.getUser()

  const logsParams = type => ({
    input: { profile_user_id: +id, actioned_by_user_id: +user.id, type },
  })

  const handleBlockFormSubmit = useCallback(
    async e => {
      setDisablingSubmit(true)
      const reason =
        blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER
          ? blockCustomText
          : blockReasonOption

      onChange({
        status: AMBASSADOR_STATUSES.blocked,
        blockedReason: reason,
        unbookMissions: forceUnbook,
      })

      //Log inserts
      await createUserProfileLog(logsParams(USER_PROFILE_LOGS_TYPE.BLOCK))
      forceUnbook &&
        (await createUserProfileLog(
          logsParams(USER_PROFILE_LOGS_TYPE.UNBOOK_ALL_MISSIONS),
        ))
      //set default state after submit
      setShowDisableForm(false)
      setBlockReasonOption('')
      setBlockCustomText('')
      setForceUnbook(false)
      setDisablingSubmit(false)
    },
    [
      blockCustomText,
      blockReasonOption,
      forceUnbook,
      onChange,
      setShowDisableForm,
      setBlockReasonOption,
      setBlockCustomText,
      setForceUnbook,
    ],
  )

  const handleBlockReasonOptionChange = useCallback(
    value => setBlockReasonOption(value),
    [setBlockReasonOption],
  )
  const handleBlockCustomTextChange = useCallback(
    e => setBlockCustomText(e.target.value),
    [setBlockCustomText],
  )

  const handleApprove = useCallback(async () => {
    onChange({ status: AMBASSADOR_STATUSES.active })
    await createUserProfileLog(logsParams(USER_PROFILE_LOGS_TYPE.APPROVE))
  }, [onChange])

  const handleShowDisableForm = () => setShowDisableForm(true)

  const options = BLOCK_AMBASSADOR_REASON_OPTIONS.map(option => ({
    label: option,
    value: option,
  }))

  const isSubmitBlockedFormDisabled =
    blockReasonOption.length === 0 ||
    (blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER &&
      blockCustomText.length === 0)

  const generateButtons = ({ documents }) => {
    let buttons = []
    const rejectedDocuments = documents
      ? documents.filter(doc => doc && doc.status == STATUS_DOCUMENT_REJECTED)
      : null
    const refuseButtonIsDisabled =
      rejectedDocuments && rejectedDocuments.length > 0 ? false : true
    if (
      [
        AMBASSADOR_STATUS_KEY.BLOCKED,
        AMBASSADOR_STATUS_KEY.TERMINATING,
        AMBASSADOR_STATUS_KEY.BOOKED_MEETING,
        AMBASSADOR_STATUS_KEY.PENDING,
        AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW,
      ].includes(status)
    ) {
      buttons.push(
        <GenericFlatButton
          type={'filled'}
          title={'Approve'}
          onClick={onApprove}
          key={AMBASSADOR_STATUSES.active}
          disabled={disabled}
        />,
      )
    }

    if (status && status !== AMBASSADOR_STATUS_KEY.BLOCKED) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Block'}
          onClick={handleShowDisableForm}
          key={AMBASSADOR_STATUSES.blocked}
          disabled={disabled || showDisableForm}
        />,
      )
    }

    if ([AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW].includes(status)) {
      buttons.push(
        <GenericFlatButton
          type={'outline'}
          title={'Refuse - send to Pre-Access'}
          onClick={showRefuseModal}
          key={AMBASSADOR_STATUSES.blocked}
          disabled={refuseButtonIsDisabled}
        />,
      )
    }

    return buttons.map(button => <Grid item>{button}</Grid>)
  }

  const dispatch = useDispatch()

  const showRefuseModal = () => {
    return dispatch(
      showReduxModal({
        title: 'Warning',
        children:
          'Please confirm if you want this user to be moved back to Pre-Access stage?',
        onConfirm: async () => {
          await onRefuse()
          await createUserProfileLog(
            logsParams(USER_PROFILE_LOGS_TYPE.REFUSE_SEND_TO_PRE_ACCESS),
          )
        },
      }),
    )
  }

  const onApprove = () => {
    const notApprovedOrNotDownloadedDocs = documents.filter(
      doc =>
        (doc &&
          [STATUS_DOCUMENT_REJECTED, STATUS_DOCUMENT_PENDING].includes(
            doc.status,
          )) ||
        !doc.status,
    )

    const haveProblemsWithDocs = notApprovedOrNotDownloadedDocs.length > 0

    if (haveProblemsWithDocs) {
      return dispatch(
        showReduxModal({
          title: 'Warning',
          children:
            'This BA has one or more rejected/missing signup files. Are you sure you want to approve them?',
          onConfirm: () => handleApprove(),
          onlyClickOnce: true,
        }),
      )
    }
    return handleApprove()
  }

  return (
    <React.Fragment>
      <Grid container spacer={3}>
        {generateButtons({ documents })}
      </Grid>
      {showDisableForm && (
        <React.Fragment>
          <Row className={classes.blockingDisclaimer}>
            <Col xs={12}>
              <p>
                The spy will be able to complete their remaining booked missions
                before being blocked
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <SelectDashboard
                label="Reason:"
                input={{
                  name: 'unbookMission',
                  value: blockReasonOption,
                  onChange: handleBlockReasonOptionChange,
                }}
                options={options}
              />
            </Col>
          </Row>
          {blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER && (
            <Row className="buttons">
              <Col xs={12}>
                <Input
                  label="Specific reason:"
                  input={{
                    value: blockCustomText,
                    onChange: handleBlockCustomTextChange,
                  }}
                  meta={{}}
                />
              </Col>
            </Row>
          )}
          <Row className="buttons">
            <Col xs={3}>
              <SubmitButton
                value="submit"
                disabled={
                  disabled || isSubmitBlockedFormDisabled || disablingSubmit
                }
                onClick={handleBlockFormSubmit}
              />
            </Col>
          </Row>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

AmbassadorStatusChange.propTypes = {
  classes: PropTypes.object.isRequired,
  ambassador: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default injectStyles(styles)(AmbassadorStatusChange)
